import { ChevronDown, Edit3, Eye, MessageCircle, MoreVertical, Pin, PinOff, Reply, Trash2 } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { usePrefersHover } from '@/hooks/use-prefers-hover'

import { ResolvedUserImage } from '@/components/common/resolved-media-image'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import {
  getMessageActionButtonsClassName,
  shouldIgnoreMessageActivation,
} from '@/utils/chat-message-action-visibility'
import { hasAttachmentWithCaption, isAttachmentMessageType } from '@/utils/chat-message-content'
import {
  getMessageSeriesBubbleClassName,
  getMessageSeriesBubblePaddingClassName,
  getMessageSeriesGroupClassName,
  getMessageSeriesMeta,
  groupMessageIndices,
} from '@/utils/chat-message-series'
import {
  getMessageViewers as getMessageViewersFromParticipants,
  MessageViewer,
} from '@/utils/chat-message-viewers'
import { formatTimestampDivider, shouldShowTimestampDivider } from '@/utils/chat-timestamp'

import { type FileMessage, ChatMessageFileContentItem, ChatMessageImageContentItem, ChatMessageVideoContentItem } from './chat-messages'
import { MessageActionButtonsContainer } from './message-action-buttons-container'
import { MessageReactionIconsRow } from './message-reaction-container'
import MessageReactions from './message-reactions'
import { LatestMessageViewerAvatars, MessageViewersDialog, MessageViewersHover } from './message-viewers'
import { MobileMessageBodyContent } from './mobile-message-body-content'
import { ReplyMessageBubble } from './replied-message-preview'
import { TimestampDivider } from './timestamp-divider'
import { useScrollToBottomVisibility } from '../hooks/use-scroll-to-bottom-visibility'

import { ConversationParticipant, Message, MessageEmojiData, MessageStatus, MessageType } from '@/types/chat.type'
import { UserAuth, UserProfile } from '@/types/user.type'

interface MobileMessagesScreenProps {
  messages: Message[]
  selectedConversationId: number
  isLoading: boolean
  isLoadingPrevious: boolean
  isLoadingMore: boolean
  hasPreviousMessages: boolean
  hasMoreMessages: boolean
  contextMessagesEndIndex: number | null
  searchQuery: string
  editingMessageId: number | null
  editMessageContent: string
  onLoadPreviousMessages: () => void
  onLoadMoreMessages: () => void
  onStartEditMessage: (messageId: number, currentContent: string) => void
  onEditMessage: () => void
  onCancelEditMessage: () => void
  onEditMessageContentChange: (content: string) => void
  onShowDeleteConfirm: (messageId: number) => void
  onStartReply: (message: Message) => void
  onMessageSenderClick: (sender: Message['sender']) => void
  renderFileMessage: (message: Message) => FileMessage | null
  formatTimestamp: (timestamp: string) => string
  authUser: UserAuth | null
  scrollManager: ScrollManager
  onScrollToMessage?: (messageId: number) => void
  onBackToConversations: () => void
  conversationTitle: string
  participants?: ConversationParticipant[]
  onMessageReact?: (messageId: number, reactionType: string, emojiData?: MessageEmojiData) => void
  highlightedMessageId?: number | null
  onPinMessage?: (messageId: number) => void
  onUnpinMessage?: (messageId: number) => void
  isTyping?: boolean
  typingUserName?: string | null
}

interface ScrollManager {
  messagesEndRef: React.RefObject<HTMLDivElement>
  messagesContainerRef: React.RefObject<HTMLDivElement>
  scrollToMessage?: (messageId: number) => void
  scrollToBottom: () => void
}

// Component to render emoji content with proper sizing for mobile
const MobileEmojiContentRenderer: React.FC<{
  content: string
  isEmojiOnly: boolean
  isOwnMessage: boolean
}> = ({ content, isOwnMessage }) => {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const sanitizedContent = React.useMemo(() => sanitizeHtmlValue(content || ''), [content])

  // Check if content truly has only icons/emojis (no regular text)
  const hasOnlyIcons = React.useMemo(() => {
    // Remove all img tags to check remaining content
    const contentWithoutImages = sanitizedContent.replace(/<img[^>]*>/gi, '')
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = contentWithoutImages
    const textWithoutImages = tempDiv.textContent || tempDiv.innerText || ''

    // Check if there are any img tags (icons/emojis)
    const hasImages = /<img[^>]*>/gi.test(sanitizedContent)

    // If there are images and no text, or only emoji Unicode characters
    if (hasImages && textWithoutImages.trim().length === 0) {
      return true
    }

    // Check if remaining text is only Unicode emojis
    if (textWithoutImages.trim().length > 0) {
      // Unicode emoji regex pattern (covers most emoji ranges)
      // Using a pattern that matches emoji characters without requiring the 'u' flag
      // eslint-disable-next-line no-misleading-character-class
      const emojiRegex =
        /(?:\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]|\uD83D[\uDE80-\uDEFF]|[\u2600-\u27BF]|\uD83E[\uDD00-\uDDFF])/g
      const textWithoutEmojis = textWithoutImages.trim().replace(emojiRegex, '').trim()
      return textWithoutEmojis.length === 0
    }

    return false
  }, [sanitizedContent])

  React.useEffect(() => {
    if (!contentRef.current) return

    const mentionLinks = contentRef.current.querySelectorAll('.mention')
    mentionLinks.forEach(link => {
      const mentionElement = link as HTMLElement
      mentionElement.style.setProperty('color', isOwnMessage ? '#ffffff' : '#1d4ed8', 'important')
      mentionElement.style.setProperty('font-weight', '700', 'important')
    })

    const images = contentRef.current.querySelectorAll('img')
    images.forEach(img => {
      // Only apply size constraints when icons are alone (no characters)
      if (hasOnlyIcons) {
        // Override inline styles to make emojis bigger
        img.style.width = '120px'
        img.style.height = '120px'
        img.style.minWidth = '120px'
        img.style.minHeight = '120px'
        img.style.maxWidth = '120px'
        img.style.maxHeight = '120px'

        // For larger screens
        if (window.innerWidth >= 640) {
          img.style.width = '150px'
          img.style.height = '150px'
          img.style.minWidth = '150px'
          img.style.minHeight = '150px'
          img.style.maxWidth = '150px'
          img.style.maxHeight = '150px'
        }
      } else {
        // Remove size constraints when icons go along with characters
        img.style.width = '24px'
        img.style.height = '24px'
        img.style.minWidth = '24px'
        img.style.minHeight = '24px'
      }
    })

    // Make Unicode emoji characters bigger only if there are only icons
    if (hasOnlyIcons) {
      const walker = document.createTreeWalker(contentRef.current, NodeFilter.SHOW_TEXT, null)

      let node
      while ((node = walker.nextNode())) {
        if (node.textContent && node.textContent.trim().length > 0) {
          // Check if text contains emoji characters
          // Using a pattern that matches emoji characters without requiring the 'u' flag
          // eslint-disable-next-line no-misleading-character-class
          const emojiRegex =
            /(?:\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]|\uD83D[\uDE80-\uDEFF]|[\u2600-\u27BF]|\uD83E[\uDD00-\uDDFF])/g
          if (emojiRegex.test(node.textContent)) {
            const parent = node.parentElement
            if (parent && parent.tagName !== 'IMG') {
              parent.style.fontSize = '60px'
              if (window.innerWidth >= 640) {
                parent.style.fontSize = '60px'
              }
            }
          }
        }
      }
    }
  }, [content, hasOnlyIcons, isOwnMessage])

  return (
    <div
      ref={contentRef}
      className={`w-full text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] hyphens-auto [&_.mention]:font-bold [&_.mention]:no-underline [&_.mention:hover]:underline ${
        isOwnMessage
          ? '[&_.mention]:text-white [&_.mention:hover]:text-white'
          : '[&_.mention]:text-blue-700 [&_.mention:hover]:text-blue-700'
      }`}
      style={hasOnlyIcons ? { fontSize: '60px', lineHeight: '1.2' } : undefined}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  )
}

export const MobileMessagesScreen: React.FC<MobileMessagesScreenProps> = ({
  messages,
  selectedConversationId,
  isLoading,
  isLoadingPrevious,
  isLoadingMore,
  hasPreviousMessages,
  hasMoreMessages,
  contextMessagesEndIndex,
  searchQuery,
  editingMessageId,
  editMessageContent,
  onLoadPreviousMessages,
  onLoadMoreMessages,
  onStartEditMessage,
  onEditMessage,
  onCancelEditMessage,
  onEditMessageContentChange,
  onShowDeleteConfirm,
  onStartReply,
  onMessageSenderClick,
  renderFileMessage,
  formatTimestamp: _formatTimestamp,
  authUser,
  scrollManager,
  onScrollToMessage,
  participants = [],
  onMessageReact,
  highlightedMessageId,
  onPinMessage,
  onUnpinMessage,
  isTyping = false,
  typingUserName = null,
}) => {
  // Handle user profile click
  const handleUserClick = (viewer: {
    id: number
    user: {
      firstName: string
      lastName: string
      avatar?: string
      profileId?: string
      id?: number
    }
    readAt: string | null
  }) => {
    // Close the viewers dialog
    setViewingMessage(null)
    // Use the same handler as message sender click to close chat popup
    // Create a user object with required id field
    const userForClick = {
      id: viewer.user.id || viewer.id,
      firstName: viewer.user.firstName,
      lastName: viewer.user.lastName,
      avatar: viewer.user.avatar,
      profileId: viewer.user.profileId,
    }
    onMessageSenderClick(userForClick)
  }

  // Use the scroll refs from the scrollManager instead of creating new ones
  const { messagesEndRef, messagesContainerRef } = scrollManager

  // Track scroll position to show/hide scroll-to-bottom button
  const { isScrolledUp } = useScrollToBottomVisibility({
    messagesContainerRef,
    messagesEndRef,
    threshold: 50,
    isLoadingPrevious,
    contentLength: messages.length,
  })

  // Refs for auto-loading functionality
  const loadPreviousObserverRef = React.useRef<IntersectionObserver | null>(null)
  const loadMoreObserverRef = React.useRef<IntersectionObserver | null>(null)
  const hasAutoLoadedRef = React.useRef(false)
  const isAutoLoadEnabledRef = React.useRef(false)

  // Delay auto-load activation when conversation changes to allow initial scroll to complete
  React.useEffect(() => {
    // Disable auto-load initially
    isAutoLoadEnabledRef.current = false

    // Enable auto-load after a delay to allow initial scroll to bottom
    const timer = setTimeout(() => {
      isAutoLoadEnabledRef.current = true
    }, 1500) // 1.5 seconds delay after conversation loads

    return () => clearTimeout(timer)
  }, [selectedConversationId])

  // Callback ref attaches an IntersectionObserver to the "Load Previous" button
  // as it mounts/unmounts, so auto-load also triggers on manual scroll-up.
  const loadPreviousButtonCallbackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (loadPreviousObserverRef.current) {
        loadPreviousObserverRef.current.disconnect()
        loadPreviousObserverRef.current = null
      }

      if (!node || isLoadingPrevious || !hasPreviousMessages) return

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (
              entry.isIntersecting &&
              !isLoadingPrevious &&
              hasPreviousMessages &&
              !hasAutoLoadedRef.current &&
              isAutoLoadEnabledRef.current
            ) {
              hasAutoLoadedRef.current = true
              onLoadPreviousMessages()
              setTimeout(() => {
                hasAutoLoadedRef.current = false
              }, 1000)
            }
          })
        },
        {
          root: messagesContainerRef.current,
          rootMargin: '100px 0px 0px 0px',
          threshold: 0,
        }
      )

      observer.observe(node)
      loadPreviousObserverRef.current = observer
    },
    [isLoadingPrevious, hasPreviousMessages, onLoadPreviousMessages, messagesContainerRef]
  )

  // Scroll listener: trigger auto-load as soon as the user scrolls UP near the top.
  // Unlike IntersectionObserver, this fires on every scroll tick so it catches the
  // case where the button is already in view when the grace period flips to true.
  // We require an upward direction so the initial auto-scroll-to-bottom (downward)
  // never mis-triggers.
  const lastScrollTopRef = React.useRef(0)
  React.useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const current = container.scrollTop
      const scrolledUp = current < lastScrollTopRef.current
      lastScrollTopRef.current = current

      if (
        scrolledUp &&
        current <= 100 &&
        hasPreviousMessages &&
        !isLoadingPrevious &&
        !hasAutoLoadedRef.current
      ) {
        hasAutoLoadedRef.current = true
        onLoadPreviousMessages()
        setTimeout(() => {
          hasAutoLoadedRef.current = false
        }, 1000)
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [hasPreviousMessages, isLoadingPrevious, onLoadPreviousMessages, messagesContainerRef])

  // Create callback ref for load more button to handle dynamic element
  const loadMoreButtonCallbackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      // Disconnect previous observer
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect()
        loadMoreObserverRef.current = null
      }

      // Set up new observer if node exists
      if (node && !isLoadingMore && hasMoreMessages && isAutoLoadEnabledRef.current) {
        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (
                entry.isIntersecting &&
                !isLoadingMore &&
                hasMoreMessages &&
                !hasAutoLoadedRef.current &&
                isAutoLoadEnabledRef.current
              ) {
                hasAutoLoadedRef.current = true
                onLoadMoreMessages()
                // Reset after a short delay to allow subsequent loads
                setTimeout(() => {
                  hasAutoLoadedRef.current = false
                }, 1000)
              }
            })
          },
          {
            root: messagesContainerRef.current,
            threshold: 0.1,
          }
        )

        observer.observe(node)
        loadMoreObserverRef.current = observer
      }
    },
    [isLoadingMore, hasMoreMessages, onLoadMoreMessages, messagesContainerRef]
  )

  // Cleanup observer on unmount
  React.useEffect(() => {
    return () => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect()
      }
      if (loadPreviousObserverRef.current) {
        loadPreviousObserverRef.current.disconnect()
      }
    }
  }, [])

  const getMessageViewers = React.useCallback(
    (message: Message) => getMessageViewersFromParticipants(message, participants),
    [participants]
  )

  const [viewingMessage, setViewingMessage] = React.useState<{ viewers: MessageViewer[] } | null>(null)

  const prefersHover = usePrefersHover()
  const [activeMessageId, setActiveMessageId] = React.useState<number | null>(null)
  const [hoveredMessageId, setHoveredMessageId] = React.useState<number | null>(null)

  const isActionButtonsVisible = React.useCallback(
    (messageId: number) => activeMessageId === messageId || (prefersHover && hoveredMessageId === messageId),
    [activeMessageId, prefersHover, hoveredMessageId]
  )

  const getActionButtonsClassName = React.useCallback(
    (messageId: number) =>
      getMessageActionButtonsClassName({
        prefersHover,
        isActive: activeMessageId === messageId,
      }),
    [prefersHover, activeMessageId]
  )

  const handleMessageActivate = React.useCallback(
    (event: React.MouseEvent, messageId: number) => {
      if (prefersHover || shouldIgnoreMessageActivation(event.target)) {
        return
      }
      setActiveMessageId(previous => (previous === messageId ? null : messageId))
    },
    [prefersHover]
  )

  React.useEffect(() => {
    if (prefersHover || activeMessageId === null) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const activeMessage = document.querySelector(`[data-message-id="${activeMessageId}"]`)
      if (activeMessage && !activeMessage.contains(event.target as Node)) {
        setActiveMessageId(null)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [prefersHover, activeMessageId])

  // Swipe gesture state
  const [swipedMessageId, setSwipedMessageId] = React.useState<number | null>(null)
  const [swipeOffset, setSwipeOffset] = React.useState(0)
  const [startX, setStartX] = React.useState(0)

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent, messageId: number) => {
    setStartX(e.touches[0].clientX)
    setSwipedMessageId(messageId)
  }

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    if (swipedMessageId === null) return

    const currentX = e.touches[0].clientX
    const deltaX = currentX - startX

    // Get the current message to determine swipe direction
    const currentMessage = messages.find(m => m.id === swipedMessageId)
    const isOwnMessage = currentMessage?.sender.id === authUser?.id

    if (isOwnMessage) {
      // For own messages: allow left swipe (negative deltaX)
      if (deltaX < 0) {
        const offset = Math.max(deltaX, -80) // Limit swipe to 80px
        setSwipeOffset(offset)
      }
    } else {
      // For other users' messages: allow right swipe (positive deltaX)
      if (deltaX > 0) {
        const offset = Math.min(deltaX, 80) // Limit swipe to 80px
        setSwipeOffset(offset)
      }
    }
  }

  // Handle touch end
  const handleTouchEnd = (message: Message) => {
    if (swipedMessageId === null) return
    // Check if swipe distance is enough to trigger reply (minimum 50px)
    const shouldTriggerReply = Math.abs(swipeOffset) >= 50

    if (shouldTriggerReply && !message.isSystem) {
      // Trigger reply action
      onStartReply(message)
    }

    // Reset swipe state
    setSwipeOffset(0)
    setSwipedMessageId(null)
  }

  // Handle touch cancel
  const handleTouchCancel = () => {
    setSwipeOffset(0)
    setSwipedMessageId(null)
  }
  // State to track which message options are open
  const [openMessageId, setOpenMessageId] = React.useState<number | null>(null)
  const messageOptionsTriggerRef = React.useRef<HTMLButtonElement | null>(null)
  const messageOptionsMenuAlignRef = React.useRef<'left' | 'right'>('left')
  const [messageOptionsCoords, setMessageOptionsCoords] = React.useState<{
    top: number
    left?: number
    right?: number
  } | null>(null)

  const openOptionsMessage = React.useMemo(
    () => (openMessageId != null ? messages.find(m => m.id === openMessageId) : undefined),
    [messages, openMessageId]
  )

  React.useLayoutEffect(() => {
    if (openMessageId === null) {
      setMessageOptionsCoords(null)
      return
    }

    const update = () => {
      const el = messageOptionsTriggerRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const align = messageOptionsMenuAlignRef.current
      const gap = 4
      if (align === 'left') {
        setMessageOptionsCoords({ top: r.bottom + gap, left: r.left })
      } else {
        setMessageOptionsCoords({ top: r.bottom + gap, right: window.innerWidth - r.right })
      }
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [openMessageId])

  // Click outside detection
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.message-options-container') && !target.closest('.message-options-portal')) {
        setOpenMessageId(null)
      }
    }

    if (openMessageId) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMessageId])

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-gray-50">
      {/* Messages Container */}
      <div className="relative flex-1 min-h-0">
      <div className="h-full overflow-y-auto min-h-0 bg-gray-50" ref={messagesContainerRef}>
        <div className="space-y-4 p-3 pb-6">
          {/* Loading More Indicator */}
          {isLoadingPrevious && (
            <div className="flex justify-center py-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-3 h-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Loading previous messages...
              </div>
            </div>
          )}

          {/* Load More Button */}
          {selectedConversationId && hasPreviousMessages && !isLoadingPrevious && messages.length > 0 && (
            <div ref={loadPreviousButtonCallbackRef} className="flex justify-center">
              <Button
                onClick={onLoadPreviousMessages}
                disabled={isLoadingPrevious}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Load Previous Messages
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="text-base font-medium">{searchQuery ? 'No messages found' : 'No messages yet'}</p>
              {searchQuery && <p className="text-sm mt-2">Try adjusting your search terms</p>}
            </div>
          ) : (
            groupMessageIndices(messages).map(groupIndices => {
              const firstMessage = messages[groupIndices[0]]
              if (!firstMessage) return null

              const isOwnGroup = firstMessage.sender.id === authUser?.id

              return (
                <div key={`series-${firstMessage.id}`} className={getMessageSeriesGroupClassName(isOwnGroup)}>
                  {groupIndices.map(index => {
              const message = messages[index]
              if (!message) return null

              const isOwnMessage = message.sender.id === authUser?.id
              const fileMessage = renderFileMessage(message)
              const isHighlighted = highlightedMessageId === message.id
              const isLatestMessage = index === messages.length - 1
              const viewers = getMessageViewers(message)
              const hasViewers = viewers.length > 0

              // Check if we should show a timestamp divider before this message
              const previousMessage = index > 0 ? messages[index - 1] : null
              const showTimestampDivider = shouldShowTimestampDivider(message, previousMessage, 30)
              const seriesMeta = getMessageSeriesMeta(messages, index)
              const seriesBubbleClassName = getMessageSeriesBubbleClassName(seriesMeta.position, isOwnMessage)
              const seriesBubblePaddingClassName = getMessageSeriesBubblePaddingClassName(seriesMeta.position)

              const hasAttachmentAndCaption = hasAttachmentWithCaption(
                message.type,
                message.content || '',
                message.fileName
              )
              const repliedMessage = message.repliedMessage
              const isReplyLayout = Boolean(repliedMessage && editingMessageId !== message.id)
              const messageSwipeStyle: React.CSSProperties = {
                transform: swipedMessageId === message.id ? `translateX(${swipeOffset}px)` : 'translateX(0)',
                transition: swipedMessageId === message.id ? 'none' : 'transform 0.3s ease-out',
              }
              const messageSwipeHandlers = {
                onTouchStart: (event: React.TouchEvent) => handleTouchStart(event, message.id),
                onTouchMove: handleTouchMove,
                onTouchEnd: () => handleTouchEnd(message),
                onTouchCancel: handleTouchCancel,
              }

              // Check if message is file/image-only (no text content)
              const isFileImageOnlyMessage =
                isAttachmentMessageType(message.type) && !hasAttachmentAndCaption

              // Check if message contains only emojis/icons (no text)
              const isEmojiOnlyMessage =
                message.type !== MessageType.FILE &&
                message.type !== MessageType.IMAGE &&
                message.type !== MessageType.VIDEO &&
                (() => {
                  // Strip HTML tags to get plain text
                  const tempDiv = document.createElement('div')
                  tempDiv.innerHTML = message.content
                  const plainText = tempDiv.textContent || tempDiv.innerText || ''

                  // Remove all img tags from HTML to check remaining content
                  const contentWithoutImages = message.content.replace(/<img[^>]*>/gi, '')
                  const tempDiv2 = document.createElement('div')
                  tempDiv2.innerHTML = contentWithoutImages
                  const textWithoutImages = tempDiv2.textContent || tempDiv2.innerText || ''

                  // Check if there are emoji img tags
                  const hasEmojiImages =
                    /<img[^>]*class="emoji"[^>]*>/gi.test(message.content) || /<img[^>]*>/gi.test(message.content)

                  // Unicode emoji regex pattern (covers most emoji ranges)
                  // Using a pattern that matches emoji characters without requiring the 'u' flag
                  // eslint-disable-next-line no-misleading-character-class
                  const emojiRegex =
                    /(?:\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]|\uD83D[\uDE80-\uDEFF]|[\u2600-\u27BF]|\uD83E[\uDD00-\uDDFF])/g

                  // Check if plain text contains only emojis and whitespace
                  const textOnlyEmojis =
                    plainText.trim().replace(emojiRegex, '').trim().length === 0 && plainText.trim().length > 0

                  // Message is emoji-only if: (has emoji images and no other text) OR (text contains only emojis)
                  return (
                    (hasEmojiImages && textWithoutImages.trim().length === 0) ||
                    (textOnlyEmojis && plainText.trim().length > 0)
                  )
                })()

              const ownBubbleClassName =
                message.status === MessageStatus.ERROR
                  ? 'bg-red-100 border border-red-300 text-red-800'
                  : message.status === MessageStatus.PENDING
                    ? 'bg-blue-500 text-white opacity-70'
                    : 'bg-blue-500 text-white'

              const otherBubbleClassName = 'border border-gray-200 bg-white text-gray-900'

              const ownMentionClassName =
                '[&_.mention]:font-bold [&_.mention]:text-white [&_.mention:hover]:text-white [&_.mention]:no-underline [&_.mention:hover]:underline'
              const otherMentionClassName =
                '[&_.mention]:font-bold [&_.mention]:text-blue-700 [&_.mention:hover]:text-blue-700 [&_.mention]:no-underline [&_.mention:hover]:underline'

              const renderMobileStatusExtras = () => (
                <>
                  {message.status === MessageStatus.ERROR && message.errorMessage && (
                    <div className="mt-2 block w-full max-w-full text-xs text-red-600 font-medium whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word]">
                      {message.errorMessage}
                    </div>
                  )}
                  {message.status === MessageStatus.PENDING && (
                    <div className="mt-2 text-xs text-blue-300 font-medium flex items-center gap-1">
                      <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse"></div>
                      Sending...
                    </div>
                  )}
                </>
              )

              const renderMobilePinnedBadge = () => {
                if (!message.pinnedMessage) return null

                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm border border-gray-200">
                          <Pin className="w-3 h-3 text-blue-600" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <div className="text-xs">
                          <div className="font-semibold">Pinned message</div>
                          {message.pinnedMessage.pinnedByUser && (
                            <div className="text-gray-600 mt-1">
                              By {message.pinnedMessage.pinnedByUser.firstName}{' '}
                              {message.pinnedMessage.pinnedByUser.lastName}
                            </div>
                          )}
                          <div className="text-gray-500 mt-0.5">
                            {formatTimestampDivider(message.pinnedMessage.pinnedAt)}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )
              }

              const renderMobileMessageExtras = () => (
                <>
                  {renderMobileStatusExtras()}
                  {renderMobilePinnedBadge()}
                </>
              )

              const renderMobileReactionIcons = () => (
                <MessageReactionIconsRow
                  messageId={message.id}
                  reactions={message.reactions || []}
                  onReact={onMessageReact}
                  currentUser={authUser as UserProfile}
                  isOwnMessage={isOwnMessage}
                  isSystemMessage={message.isSystem}
                />
              )

              const renderMobileActionButtons = (
                menuAlign: 'left' | 'right',
                options?: { requirePinHandlersForMenu?: boolean }
              ) => {
                if (swipedMessageId === message.id) return null

                const showMenu =
                  !message.isSystem &&
                  (!options?.requirePinHandlersForMenu || onPinMessage || onUnpinMessage)

                return (
                  <MessageActionButtonsContainer
                    className={getActionButtonsClassName(message.id)}
                    isVisible={isActionButtonsVisible(message.id)}
                    boundaryRef={messagesContainerRef}
                  >
                    {!message.isSystem && (
                      <button
                        onClick={() => onStartReply(message)}
                        className="p-1.5 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Reply to message"
                        disabled={editingMessageId === message.id || message.isSystem}
                      >
                        <Reply className="w-3 h-3 text-gray-500 hover:text-blue-600 transition-colors duration-200" />
                      </button>
                    )}

                    {!message.isSystem && onMessageReact && (
                      <div className="inline-flex">
                        <MessageReactions
                          messageId={message.id}
                          reactions={message.reactions || []}
                          onReact={onMessageReact}
                          currentUser={authUser as UserProfile}
                          isOwnMessage={isOwnMessage}
                          buttonOnly={true}
                        />
                      </div>
                    )}

                    {showMenu && (
                      <div className="flex items-center">
                        <div className="relative message-options-container">
                          <button
                            onClick={e => {
                              if (openMessageId === message.id) {
                                messageOptionsTriggerRef.current = null
                                setOpenMessageId(null)
                              } else {
                                messageOptionsMenuAlignRef.current = menuAlign
                                messageOptionsTriggerRef.current = e.currentTarget
                                setOpenMessageId(message.id)
                              }
                            }}
                            className="p-1.5 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative z-10"
                            disabled={editingMessageId === message.id || message.isSystem}
                          >
                            <MoreVertical className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </MessageActionButtonsContainer>
                )
              }

              const wrapMobileMessageWithReactions = (bubble: React.ReactNode) => bubble

              return (
                <React.Fragment key={message.id}>
                  {/* Timestamp Divider */}
                  {showTimestampDivider && (
                    <TimestampDivider
                      timestamp={message.createdAt}
                      locale="vi"
                      formatFunction={formatTimestampDivider}
                    />
                  )}

                  <div
                    data-message-id={message.id}
                    className={`flex w-full origin-center ${isOwnMessage ? 'justify-end' : 'justify-start'} group ${
                      isHighlighted ? 'animate-message-pop' : ''
                    }`}
                    onMouseEnter={() => setHoveredMessageId(message.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    {isOwnMessage ? (
                      /* Own message layout - 3 layers: avatar (hidden for own), message box with actions outside, reactions */
                      <div className="flex w-full flex-col items-end">
                        {/* Layer 2: actions to the left of bubble, group aligned end */}
                        <div className="flex w-full justify-end">
                          <div
                            className={isReplyLayout ? 'w-full min-w-0' : 'flex items-center gap-2 min-w-0'}
                            onClick={event => handleMessageActivate(event, message.id)}
                          >
                            {!isReplyLayout && renderMobileActionButtons('left')}

                            {/* Message box */}
                            <div className="flex min-w-0 flex-col items-end">
                            <MessageViewersHover viewers={viewers}>
                              <div className="relative min-w-0">
                            {(isEmojiOnlyMessage || isFileImageOnlyMessage) &&
                            !isHighlighted &&
                            message.status !== MessageStatus.ERROR &&
                            !message.repliedMessage &&
                            !message.pinnedMessage &&
                            editingMessageId !== message.id ? (
                              wrapMobileMessageWithReactions(
                                <div className="relative">
                                  {fileMessage ? (
                                    fileMessage.isImage ? (
                                      <ChatMessageImageContentItem fileData={fileMessage} />
                                    ) : fileMessage.isVideo ? (
                                      <ChatMessageVideoContentItem fileData={fileMessage} />
                                    ) : (
                                      <ChatMessageFileContentItem fileData={fileMessage} />
                                    )
                                  ) : (
                                    <MobileEmojiContentRenderer
                                      content={message.content}
                                      isEmojiOnly={isEmojiOnlyMessage}
                                      isOwnMessage={isOwnMessage}
                                    />
                                  )}
                                </div>
                              )
                            ) : (
                              wrapMobileMessageWithReactions(
                              <div
                                className={
                                  isReplyLayout || hasAttachmentAndCaption
                                    ? 'relative w-full transition-all duration-200'
                                    : `relative w-fit max-w-full px-3 py-2 text-sm transition-all duration-200 shadow-sm ${seriesBubbleClassName} ${seriesBubblePaddingClassName} ${ownBubbleClassName}`
                                }
                                style={isReplyLayout ? undefined : messageSwipeStyle}
                                {...(isReplyLayout ? {} : messageSwipeHandlers)}
                              >
                                {isReplyLayout && repliedMessage ? (
                                  <ReplyMessageBubble
                                    isOwnMessage={isOwnMessage}
                                    seriesBubbleClassName={seriesBubbleClassName}
                                    repliedMessage={repliedMessage}
                                    onScrollToMessage={onScrollToMessage}
                                    bubbleClassName={`${seriesBubblePaddingClassName} ${ownBubbleClassName}`}
                                    splitBubbles={hasAttachmentAndCaption}
                                    actionButtons={renderMobileActionButtons('left')}
                                    style={messageSwipeStyle}
                                    {...messageSwipeHandlers}
                                  >
                                    <div>
                                      <MobileMessageBodyContent
                                        message={message}
                                        fileMessage={fileMessage}
                                        isOwnMessage={isOwnMessage}
                                        isEmojiOnlyMessage={isEmojiOnlyMessage}
                                        seriesPosition={seriesMeta.position}
                                        bubbleClassName={ownBubbleClassName}
                                        mentionClassName={ownMentionClassName}
                                        MobileEmojiContentRenderer={MobileEmojiContentRenderer}
                                        extras={hasAttachmentAndCaption ? renderMobileMessageExtras() : undefined}
                                      />
                                      {!hasAttachmentAndCaption && renderMobileMessageExtras()}
                                    </div>
                                  </ReplyMessageBubble>
                                ) : editingMessageId === message.id ? (
                                  <div className="space-y-2">
                                    <div className="text-xs text-blue-200 font-medium mb-1">✏️ Editing message...</div>
                                    <div className="relative">
                                      <textarea
                                        value={editMessageContent}
                                        onChange={e => onEditMessageContentChange(e.target.value)}
                                        className="text-xs border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white pr-16 w-full rounded p-2 text-gray-900"
                                        autoFocus
                                        placeholder="Edit your message..."
                                        maxLength={500}
                                        rows={3}
                                      />
                                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                                        {editMessageContent.length}/{500}
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        size="sm"
                                        onClick={onEditMessage}
                                        className="text-xs bg-white hover:bg-red-50 text-gray-900 border border-gray-300 hover:border-red-300 hover:text-red-700"
                                        disabled={
                                          !editMessageContent.trim() ||
                                          editMessageContent === message.content ||
                                          editMessageContent.length > 500
                                        }
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={onCancelEditMessage}
                                        className="text-xs border-gray-300 text-gray-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <MobileMessageBodyContent
                                      message={message}
                                      fileMessage={fileMessage}
                                      isOwnMessage={isOwnMessage}
                                      isEmojiOnlyMessage={isEmojiOnlyMessage}
                                      seriesPosition={seriesMeta.position}
                                      bubbleClassName={ownBubbleClassName}
                                      mentionClassName={ownMentionClassName}
                                      MobileEmojiContentRenderer={MobileEmojiContentRenderer}
                                      extras={hasAttachmentAndCaption ? renderMobileMessageExtras() : undefined}
                                    />
                                    {!hasAttachmentAndCaption && renderMobileMessageExtras()}
                                  </div>
                                )}
                              </div>
                              )
                            )}
                              </div>
                            </MessageViewersHover>
                            {isLatestMessage && hasViewers && (
                              <LatestMessageViewerAvatars
                                viewers={viewers}
                                align="end"
                                onClick={() => setViewingMessage({ viewers })}
                              />
                            )}
                            </div>
                          </div>
                        </div>
                        {renderMobileReactionIcons()}
                      </div>
                    ) : (
                      /* Other user message layout */
                      <div className="flex w-full items-end gap-2">
                        <div className="w-8 flex-shrink-0 self-end">
                          {seriesMeta.showSenderHeader ? (
                            <ResolvedUserImage
                              avatar={message.sender.avatar}
                              alt={`${message.sender.firstName} ${message.sender.lastName}`}
                              size={32}
                              className="h-8 w-8 cursor-pointer rounded-full"
                              placeholderClassName="h-8 w-8"
                              onClick={() => onMessageSenderClick(message.sender)}
                            />
                          ) : null}
                        </div>

                        <div className="flex min-w-0 flex-1 flex-col items-start">
                          {seriesMeta.showSenderHeader && (
                            <button
                              onClick={() => onMessageSenderClick(message.sender)}
                              className="mb-0.5 cursor-pointer text-xs font-medium text-gray-600 transition-colors hover:text-blue-600"
                              title={`View ${message.sender.firstName} ${message.sender.lastName}'s profile`}
                            >
                              {`${message.sender.firstName} ${message.sender.lastName}`}
                            </button>
                          )}

                          <div className="flex w-full justify-start">
                          <div
                            className={isReplyLayout ? 'w-full min-w-0' : 'flex items-center gap-2 min-w-0'}
                            onClick={event => handleMessageActivate(event, message.id)}
                          >
                            {/* Message box */}
                            <div className="relative w-fit max-w-full min-w-0">
                            {wrapMobileMessageWithReactions(
                            <div
                              className={
                                isReplyLayout || hasAttachmentAndCaption
                                  ? 'relative w-full max-w-full transition-all duration-200'
                                  : `relative w-fit max-w-full px-3 py-2 text-sm transition-all duration-200 shadow-sm ${seriesBubbleClassName} ${seriesBubblePaddingClassName} ${
                                      isEmojiOnlyMessage || isFileImageOnlyMessage
                                        ? 'bg-transparent'
                                        : otherBubbleClassName
                                    }`
                              }
                              style={isReplyLayout ? undefined : messageSwipeStyle}
                              {...(isReplyLayout ? {} : messageSwipeHandlers)}
                            >
                              {isReplyLayout && repliedMessage ? (
                                <ReplyMessageBubble
                                  isOwnMessage={isOwnMessage}
                                  seriesBubbleClassName={seriesBubbleClassName}
                                  repliedMessage={repliedMessage}
                                  onScrollToMessage={onScrollToMessage}
                                  bubbleClassName={`${seriesBubblePaddingClassName} ${otherBubbleClassName}`}
                                  splitBubbles={hasAttachmentAndCaption}
                                  actionButtons={renderMobileActionButtons('right', { requirePinHandlersForMenu: true })}
                                  style={messageSwipeStyle}
                                  {...messageSwipeHandlers}
                                >
                                  <div>
                                    <MobileMessageBodyContent
                                      message={message}
                                      fileMessage={fileMessage}
                                      isOwnMessage={isOwnMessage}
                                      isEmojiOnlyMessage={isEmojiOnlyMessage}
                                      seriesPosition={seriesMeta.position}
                                      bubbleClassName={otherBubbleClassName}
                                      mentionClassName={otherMentionClassName}
                                      MobileEmojiContentRenderer={MobileEmojiContentRenderer}
                                      extras={hasAttachmentAndCaption ? renderMobileMessageExtras() : undefined}
                                    />
                                    {!hasAttachmentAndCaption && renderMobileMessageExtras()}
                                  </div>
                                </ReplyMessageBubble>
                              ) : editingMessageId === message.id ? (
                                <div className="space-y-2">
                                  <div className="text-xs text-blue-200 font-medium mb-1">✏️ Editing message...</div>
                                  <div className="relative">
                                    <textarea
                                      value={editMessageContent}
                                      onChange={e => onEditMessageContentChange(e.target.value)}
                                      className="text-xs border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white pr-16 w-full rounded p-2 text-gray-900"
                                      autoFocus
                                      placeholder="Edit your message..."
                                      maxLength={500}
                                      rows={3}
                                    />
                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                                      {editMessageContent.length}/{500}
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      onClick={onEditMessage}
                                      className="text-xs bg-white hover:bg-red-50 text-gray-900 border border-gray-300 hover:border-red-300 hover:text-red-700"
                                      disabled={
                                        !editMessageContent.trim() ||
                                        editMessageContent === message.content ||
                                        editMessageContent.length > 500
                                      }
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={onCancelEditMessage}
                                      className="text-xs border-gray-300 text-gray-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <MobileMessageBodyContent
                                    message={message}
                                    fileMessage={fileMessage}
                                    isOwnMessage={isOwnMessage}
                                    isEmojiOnlyMessage={isEmojiOnlyMessage}
                                    seriesPosition={seriesMeta.position}
                                    bubbleClassName={otherBubbleClassName}
                                    mentionClassName={otherMentionClassName}
                                    MobileEmojiContentRenderer={MobileEmojiContentRenderer}
                                    extras={hasAttachmentAndCaption ? renderMobileMessageExtras() : undefined}
                                  />
                                  {!hasAttachmentAndCaption && renderMobileMessageExtras()}
                                </div>
                              )}
                            </div>
                            )}
                            </div>

                            {!isReplyLayout &&
                              renderMobileActionButtons('right', { requirePinHandlersForMenu: true })}
                          </div>
                          </div>
                          {renderMobileReactionIcons()}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Show Load Newer Messages button after context messages */}
                  {contextMessagesEndIndex !== null &&
                    index === contextMessagesEndIndex &&
                    hasMoreMessages &&
                    !isLoadingMore && (
                      <div ref={loadMoreButtonCallbackRef} className="flex justify-center py-2">
                        <Button
                          onClick={onLoadMoreMessages}
                          disabled={isLoadingMore}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          Load More Messages
                        </Button>
                      </div>
                    )}

                  {/* Show loading indicator for newer messages */}
                  {contextMessagesEndIndex !== null && index === contextMessagesEndIndex && isLoadingMore && (
                    <div className="flex justify-center py-2">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <div className="w-3 h-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Loading more messages...
                      </div>
                    </div>
                  )}
                </React.Fragment>
              )
                  })}
                </div>
              )
            })
          )}

          {/* Scroll to bottom reference */}
          <div ref={messagesEndRef} />

          {isTyping && (
            <div className="flex justify-start py-1">
              <div className="rounded-2xl bg-gray-100 px-3 py-2 text-xs text-gray-600 border border-gray-200">
                {typingUserName ? `${typingUserName} is typing...` : 'Typing...'}
              </div>
            </div>
          )}
        </div>
      </div>

      {isScrolledUp && (
        <div className="pointer-events-none absolute bottom-4 left-0 right-0 z-10 flex justify-center">
          <Button
            onClick={scrollManager.scrollToBottom}
            size="sm"
            variant="outline"
            className="pointer-events-auto h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 backdrop-blur-sm"
            title="Scroll to bottom"
          >
            <ChevronDown className="h-5 w-5 text-gray-600" />
          </Button>
        </div>
      )}
      </div>

      {openMessageId != null &&
        messageOptionsCoords &&
        openOptionsMessage &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="message-options-portal fixed z-[10000] w-44 max-w-[min(12rem,calc(100vw-1rem))] sm:w-48 sm:max-w-[12rem] max-h-[min(50vh,16rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
            style={{
              top: messageOptionsCoords.top,
              ...(messageOptionsCoords.left !== undefined
                ? { left: messageOptionsCoords.left, right: 'auto' as const }
                : { right: messageOptionsCoords.right, left: 'auto' as const }),
            }}
          >
            <div className="p-2">
              <div className="space-y-1">
                {openOptionsMessage.sender.id === authUser?.id ? (
                  <>
                    {(onPinMessage || onUnpinMessage) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (openOptionsMessage.pinnedMessage && onUnpinMessage) {
                            onUnpinMessage(openOptionsMessage.id)
                          } else if (!openOptionsMessage.pinnedMessage && onPinMessage) {
                            onPinMessage(openOptionsMessage.id)
                          }
                          setOpenMessageId(null)
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={editingMessageId === openOptionsMessage.id || openOptionsMessage.isSystem}
                      >
                        {openOptionsMessage.pinnedMessage ? (
                          <>
                            <PinOff className="w-3 h-3 text-gray-500 flex-shrink-0" />
                            <span className="truncate">Unpin message</span>
                          </>
                        ) : (
                          <>
                            <Pin className="w-3 h-3 text-blue-500 flex-shrink-0" />
                            <span className="truncate">Pin message</span>
                          </>
                        )}
                      </button>
                    )}
                    {getMessageViewers(openOptionsMessage).length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setViewingMessage({ viewers: getMessageViewers(openOptionsMessage) })
                          setOpenMessageId(null)
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      >
                        <Eye className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        <span className="truncate">Người đã xem</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        onStartEditMessage(openOptionsMessage.id, openOptionsMessage.content)
                        setOpenMessageId(null)
                      }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={editingMessageId === openOptionsMessage.id || openOptionsMessage.isSystem}
                    >
                      <Edit3 className="w-3 h-3 text-blue-500 flex-shrink-0" />
                      <span className="truncate">Edit message</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onShowDeleteConfirm(openOptionsMessage.id)
                        setOpenMessageId(null)
                      }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={editingMessageId === openOptionsMessage.id}
                    >
                      <Trash2 className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">Delete message</span>
                    </button>
                  </>
                ) : (
                  <>
                    {(onPinMessage || onUnpinMessage) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (openOptionsMessage.pinnedMessage && onUnpinMessage) {
                            onUnpinMessage(openOptionsMessage.id)
                          } else if (!openOptionsMessage.pinnedMessage && onPinMessage) {
                            onPinMessage(openOptionsMessage.id)
                          }
                          setOpenMessageId(null)
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={editingMessageId === openOptionsMessage.id || openOptionsMessage.isSystem}
                      >
                        {openOptionsMessage.pinnedMessage ? (
                          <>
                            <PinOff className="w-3 h-3 text-gray-500 flex-shrink-0" />
                            <span className="truncate">Unpin message</span>
                          </>
                        ) : (
                          <>
                            <Pin className="w-3 h-3 text-blue-500 flex-shrink-0" />
                            <span className="truncate">Pin message</span>
                          </>
                        )}
                      </button>
                    )}
                    {getMessageViewers(openOptionsMessage).length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setViewingMessage({ viewers: getMessageViewers(openOptionsMessage) })
                          setOpenMessageId(null)
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      >
                        <Eye className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        <span className="truncate">Người đã xem</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {viewingMessage && (
        <MessageViewersDialog
          viewers={viewingMessage.viewers}
          onClose={() => setViewingMessage(null)}
          onUserClick={handleUserClick}
        />
      )}
    </div>
  )
}
